import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { Tooltip } from 'src/core/ui';
import { useUserHasTagTypePermissions } from 'src/hooks/useUserHasTagTypePermissions';
import { PersistentTagIcon } from 'src/svgs/PersistentTagIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { ActionOutboxEntityType, Tag } from 'src/WebApiController';

import { EditTagMetadataDialog } from '../TagMetadataForm/EditTagMetadataDialog';
import * as styles from '../TagsFormBody.css';
import { TagValueDisplay, TagValueInputCell } from '../TagValueCell';
import { useTagMetadataTitle } from './useTagMetadataTitle';

export const TagsListItem = ({
  tagMetadata,
  tagsWithValue,
  disabled,
  onTagChange,
  entityType,
}: {
  tagMetadata: Tag;
  // All tags with value for the entity type
  tagsWithValue: Tag[];
  disabled?: boolean;
  onTagChange: (tagMetadata: Tag, newValues: Tag[]) => void;
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent;
}) => {
  const { metadataTitle, inheritedFromText } = useTagMetadataTitle(
    tagMetadata,
    entityType
  );

  const tagValues = useMemo(() => {
    return tagsWithValue.filter(
      (t) =>
        t.key === tagMetadata.key &&
        t.sourceEntityId === tagMetadata.sourceEntityId
    );
  }, [tagsWithValue, tagMetadata]);

  const {
    openDialog: openEditTagMetadataDialog,
    closeDialog: closeEditTagMetadataDialog,
  } = useDialog(DialogId.EditTagMetadata, EditTagMetadataDialog);

  const { hasEditTagTypePermission } = useUserHasTagTypePermissions(entityType);

  return (
    <div className={styles.tagRowV3} title={metadataTitle}>
      <div className={styles.persistentTagIconContainer}>
        {tagMetadata?.persistentTagId != null ? (
          <>
            {inheritedFromText ? (
              <Tooltip
                triggerContent={
                  <div>
                    <PersistentTagIcon fill={vars.color.textStrong} />
                  </div>
                }
                align="center"
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'unset',
                    textOverflow: 'unset',
                    minWidth: 'fit-content',
                  }}
                >
                  {inheritedFromText}
                </div>
              </Tooltip>
            ) : (
              <PersistentTagIcon fill={vars.color.textStrong} />
            )}
          </>
        ) : (
          <></>
        )}
      </div>
      <div
        className={clsx(styles.textSecondary, {
          [styles.clickable]: hasEditTagTypePermission && !disabled,
        })}
        onClick={() => {
          if (disabled || !hasEditTagTypePermission) {
            return;
          }
          openEditTagMetadataDialog({
            tagMetadataToEdit: tagMetadata,
            tagsWithValue: tagsWithValue,
            onTagChange: onTagChange,
            onClose: closeEditTagMetadataDialog,
            entityType,
          });
        }}
      >
        {tagMetadata.key}
      </div>
      <hr className={styles.dividerV3} />
      <TagsValueInput
        disabled={disabled}
        onTagChange={onTagChange}
        tagMetadata={tagMetadata}
        tagValues={tagValues}
      />
    </div>
  );
};

const TagsValueInput = ({
  disabled,
  onTagChange,
  tagMetadata,
  tagValues,
}: {
  disabled?: boolean;
  onTagChange: (tagMetadata: Tag, newValues: Tag[]) => void;
  tagMetadata: Tag;
  tagValues: Tag[];
}) => {
  const stackRef = useRef<HTMLDivElement>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const tagValuesWithPlaceholder = useMemo(() => {
    const needPlaceholderTag =
      tagValues.length === 0 || !tagMetadata.singleValueOnly;

    if (needPlaceholderTag) {
      return [
        ...tagValues,
        {
          ...tagMetadata,
          value: '',
        },
      ];
    }

    return tagValues;
  }, [tagMetadata, tagValues]);

  const tagValuesWithPlaceholderLastIndex = tagValuesWithPlaceholder.length - 1;

  const onTagValueChange = useCallback(
    (index: number, newValue: string) => {
      let newValues = [...tagValuesWithPlaceholder];
      newValues[index].value = newValue;
      // Remove any empty values, especially for the placeholder
      newValues = newValues.filter((tag) => !!tag.value);
      onTagChange(tagMetadata, newValues);
    },
    [tagMetadata, tagValuesWithPlaceholder, onTagChange]
  );

  const onBlur = useCallback(() => {
    setEditingIndex(null);
  }, []);

  const onClick = useCallback((index: number) => {
    setEditingIndex(index);
  }, []);

  // scroll to the right
  useEffect(() => {
    if (stackRef.current) {
      stackRef.current.scrollTo({
        left: stackRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  }, [tagValuesWithPlaceholder]);

  return (
    <Stack direction="row" gap={'l'} alignItems={'center'}>
      <Stack
        direction={'row'}
        gap={'l'}
        alignItems={'center'}
        style={{ maxWidth: '400px', overflowX: 'auto', flexShrink: 1 }}
        ref={stackRef}
      >
        {tagValuesWithPlaceholder.slice(0, -1).map((tag, index) => (
          <SingleTagValueInput
            key={index}
            index={index}
            disabled={disabled}
            isEditing={editingIndex === index}
            onBlur={onBlur}
            onClick={onClick}
            tag={tag}
            tagValues={tagValues}
            onChange={onTagValueChange}
          />
        ))}
      </Stack>
      <SingleTagValueInput
        key={tagValuesWithPlaceholderLastIndex}
        index={tagValuesWithPlaceholderLastIndex}
        disabled={disabled}
        isEditing={editingIndex === tagValuesWithPlaceholderLastIndex}
        onBlur={onBlur}
        onClick={onClick}
        tag={tagValuesWithPlaceholder[tagValuesWithPlaceholderLastIndex]}
        tagValues={tagValues}
        onChange={onTagValueChange}
      />
    </Stack>
  );
};

const SingleTagValueInput = ({
  index,
  disabled,
  isEditing,
  onBlur,
  tag,
  tagValues,
  onChange,
  onClick,
}: {
  index: number;
  disabled?: boolean;
  isEditing: boolean;
  onBlur: () => void;
  tag: Tag;
  tagValues: Tag[];
  onChange: (index: number, newValue: string) => void;
  onClick: (index: number) => void;
}) => {
  return (
    <div
      style={{
        height: '100%',
        width: 'min-content',
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {isEditing ? (
        <TagValueInputCell
          onBlur={onBlur}
          tag={tag}
          disabled={disabled}
          tagsCurrentEntity={tagValues}
          onChange={(newValue) => {
            onChange(index, newValue);
            onBlur();
          }}
        />
      ) : (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick(index);
          }}
          style={{
            height: 'min-content',
            width: 'min-content',
          }}
        >
          {tag.value ? (
            <TagValueDisplay tag={tag} displayMode />
          ) : (
            <Button
              disabled={disabled}
              variant={'link'}
              style={{ whiteSpace: 'nowrap', padding: 0 }}
            >
              <Content id={ContentId.AddValue} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
